import {
  Chain,
  createPublicClient,
  createTransport,
  http,
  PublicClient,
  type Transport,
} from 'viem';
import {
  arbitrum,
  avalanche,
  base,
  mainnet,
  optimism,
  polygon,
  ronin,
  sepolia,
} from 'viem/chains';
import { z } from 'zod';
import { toChecksumAddress } from './address';
import type { AllowedLogFields } from './monitoring';

// Define the enum with meaningful names
export enum NetworkId {
  ETHEREUM = 1,
  OPTIMISM = 10,
  POLYGON = 137,
  RONIN = 2020,
  BASE = 8453,
  ARBITRUM = 42161,
  AVALANCHE = 43114,
  SMAUG = 8421, // Smaug Testnet (local Anvil or Tenderly Virtual TestNet)
  ETHEREUM_FORK = 9991, // Ethereum Fork for Tenderly Virtual TestNet
  ETHEREUM_CONTRACT_DEPLOYMENT_FORK = 123123, // Eth fork on Tenderly to test contract deployment on Eth
  BASE_FORK = 9998453, // Base Fork for Tenderly Virtual TestNet
  ARBITRUM_FORK = 99942161, // Arbitrum Fork for Tenderly Virtual TestNet
  RONIN_FORK = 9992020, // Ronin Fork for Tenderly Virtual TestNet
  ETHEREUM_SEPOLIA = 11155111, // Sepolia for testing contract deployment on real Eth infrastructure
}

// Use z.nativeEnum for the schema with branding
export const networkTypeSchema = z.enum(NetworkId).brand<'NETWORK_ID'>();

export type NetworkType = z.infer<typeof networkTypeSchema>;

export const ALL_SUPPORTED_NETWORKS: NetworkType[] = [
  networkTypeSchema.parse(NetworkId.ETHEREUM),
  networkTypeSchema.parse(NetworkId.OPTIMISM),
  networkTypeSchema.parse(NetworkId.POLYGON),
  networkTypeSchema.parse(NetworkId.RONIN),
  networkTypeSchema.parse(NetworkId.BASE),
  networkTypeSchema.parse(NetworkId.ARBITRUM),
  networkTypeSchema.parse(NetworkId.AVALANCHE),
  networkTypeSchema.parse(NetworkId.SMAUG),
  networkTypeSchema.parse(NetworkId.ETHEREUM_FORK),
  networkTypeSchema.parse(NetworkId.BASE_FORK),
  networkTypeSchema.parse(NetworkId.ARBITRUM_FORK),
  networkTypeSchema.parse(NetworkId.RONIN_FORK),
  networkTypeSchema.parse(NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK),
  networkTypeSchema.parse(NetworkId.ETHEREUM_SEPOLIA),
];

/** Canonical networks enabled in the production environment. */
export const PRODUCTION_NETWORKS: readonly NetworkType[] = [
  networkTypeSchema.parse(NetworkId.ETHEREUM),
  networkTypeSchema.parse(NetworkId.OPTIMISM),
  networkTypeSchema.parse(NetworkId.RONIN),
  networkTypeSchema.parse(NetworkId.BASE),
  networkTypeSchema.parse(NetworkId.ARBITRUM),
];

// Determine networks based on environment
export const SUPPORTED_NETWORKS: NetworkType[] = (() => {
  switch (process.env.ENVIRONMENT) {
    // Case: Prod - only include enabled production networks
    case 'PROD':
      return [...PRODUCTION_NETWORKS];
    // Case: Sandbox - only include forked networks
    case 'SANDBOX':
      return [
        networkTypeSchema.parse(NetworkId.ETHEREUM_FORK),
        networkTypeSchema.parse(NetworkId.BASE_FORK),
        networkTypeSchema.parse(NetworkId.ARBITRUM_FORK),
        networkTypeSchema.parse(NetworkId.RONIN_FORK),
      ];
    // Case: Otherwise, include all networks
    case 'LOCAL':
      return [
        networkTypeSchema.parse(NetworkId.SMAUG),
        networkTypeSchema.parse(NetworkId.ETHEREUM_FORK),
        networkTypeSchema.parse(NetworkId.BASE_FORK),
        networkTypeSchema.parse(NetworkId.ARBITRUM_FORK),
        networkTypeSchema.parse(NetworkId.RONIN_FORK),
      ];
    case 'STAGE':
      return [
        networkTypeSchema.parse(NetworkId.SMAUG),
        networkTypeSchema.parse(NetworkId.ETHEREUM_FORK),
        networkTypeSchema.parse(NetworkId.BASE_FORK),
        networkTypeSchema.parse(NetworkId.ARBITRUM_FORK),
        networkTypeSchema.parse(NetworkId.RONIN_FORK),
        networkTypeSchema.parse(NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK),
        networkTypeSchema.parse(NetworkId.ETHEREUM_SEPOLIA),
      ];
    case 'CI':
    case 'DEMO':
    case 'PREPROD':
    default:
      return ALL_SUPPORTED_NETWORKS;
  }
})();

// Mapping of network IDs to display names
export const NETWORK_OPTIONS: { id: NetworkType; name: string }[] =
  SUPPORTED_NETWORKS.map((networkId) => ({
    id: networkId,
    name: getNetworkDisplayName(networkId),
  }));

// Function to get the display name of a network
export function getNetworkDisplayName(network: NetworkId): string {
  const names: Record<NetworkId, string> = {
    [NetworkId.ETHEREUM]: 'Ethereum',
    [NetworkId.POLYGON]: 'Polygon',
    [NetworkId.RONIN]: 'Ronin',
    [NetworkId.OPTIMISM]: 'Optimism',
    [NetworkId.ARBITRUM]: 'Arbitrum',
    [NetworkId.BASE]: 'Base',
    [NetworkId.AVALANCHE]: 'Avalanche',
    [NetworkId.SMAUG]: 'Smaug Testnet',
    [NetworkId.ETHEREUM_FORK]: 'Ethereum Fork',
    [NetworkId.BASE_FORK]: 'Base Fork',
    [NetworkId.ARBITRUM_FORK]: 'Arbitrum Fork',
    [NetworkId.RONIN_FORK]: 'Ronin Fork',
    [NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK]:
      'Ethereum Contract Deployment Fork',
    [NetworkId.ETHEREUM_SEPOLIA]: 'Ethereum Sepolia',
  };
  return names[network];
}

/**
 * Returns whether a network is a local, testnet, or forked network.
 */
export function isLocalOrTestnetNetwork(
  network: NetworkType | NetworkId
): boolean {
  switch (Number(network)) {
    case NetworkId.SMAUG:
    case NetworkId.ETHEREUM_FORK:
    case NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK:
    case NetworkId.BASE_FORK:
    case NetworkId.ARBITRUM_FORK:
    case NetworkId.RONIN_FORK:
    case NetworkId.ETHEREUM_SEPOLIA:
      return true;
    default:
      return false;
  }
}

const PUBLIC_RPC_RETRIES_PER_URL = 10;

export type PublicRpcFailureLogger = (
  data: AllowedLogFields
) => void | Promise<void>;

export type NetworkViemClientOptions = {
  usePublicRpcUrls?: boolean;
  onPublicRpcFailure?: PublicRpcFailureLogger;
};

/**
 * Public RPC endpoints are opt-in fallbacks for reads from clients that do not
 * have backend private/preferred RPC credentials, such as the mobile app.
 */
const PUBLIC_RPC_URLS: Partial<Record<NetworkId, string[]>> = {
  [NetworkId.ETHEREUM]: [
    'https://ethereum-rpc.publicnode.com',
    'https://eth.drpc.org',
    'https://eth-mainnet.public.blastapi.io',
    'https://gateway.tenderly.co/public/mainnet',
    'https://uk.rpc.blxrbdn.com',
  ],
  [NetworkId.POLYGON]: [
    'https://polygon-bor-rpc.publicnode.com',
    'https://polygon.publicnode.com',
    'https://polygon.drpc.org',
    'https://polygon.api.onfinality.io/public',
    'https://gateway.tenderly.co/public/polygon',
  ],
  [NetworkId.RONIN]: [
    'https://api.roninchain.com/rpc',
    'https://ronin.gateway.tenderly.co',
  ],
  [NetworkId.OPTIMISM]: [
    'https://optimism.gateway.tenderly.co',
    'https://optimism.api.onfinality.io/public',
    'https://optimism.public.blockpi.network/v1/rpc/public',
    'https://optimism-rpc.publicnode.com',
    'https://optimism.drpc.org',
  ],
  [NetworkId.ARBITRUM]: [
    'https://arb1.lava.build',
    'https://arbitrum.gateway.tenderly.co',
    'https://arbitrum-one.public.blastapi.io',
    'https://arbitrum.public.blockpi.network/v1/rpc/public',
    'https://arbitrum-one-public.nodies.app',
  ],
  [NetworkId.BASE]: [
    'https://mainnet.base.org',
    'https://base-rpc.publicnode.com',
    'https://base.drpc.org',
    'https://gateway.tenderly.co/public/base',
    'https://base-mainnet.public.blastapi.io',
  ],
  [NetworkId.AVALANCHE]: [
    'https://avalanche.rpc.sentio.xyz',
    'https://avalanche.api.onfinality.io/public/ext/bc/C/rpc',
    'https://1rpc.io/avax/c',
    'https://avalanche-c-chain-rpc.publicnode.com',
    'https://avalanche.drpc.org',
  ],
  [NetworkId.ETHEREUM_SEPOLIA]: [
    'https://ethereum-sepolia-rpc.publicnode.com',
    'https://ethereum-sepolia.publicnode.com',
    'https://sepolia.drpc.org',
    'https://gateway.tenderly.co/public/sepolia',
    'https://ethereum-sepolia-public.nodies.app',
  ],
};

function isValidRpcUrl(rpcUrl: string | null | undefined): rpcUrl is string {
  return Boolean(rpcUrl && !rpcUrl.includes('undefined'));
}

// Function to get the private/preferred RPC URL of a network
function getPrivateNetworkRpcUrl(network: NetworkType): string | null {
  // Handle Smaug specially - use local Anvil for LOCAL/CI/DEMO, Tenderly otherwise.
  if (network === networkTypeSchema.parse(NetworkId.SMAUG)) {
    if (
      process.env.ENVIRONMENT === 'LOCAL' ||
      process.env.ENVIRONMENT === 'CI' ||
      process.env.ENVIRONMENT === 'DEMO'
    ) {
      return 'http://localhost:8545';
    }
    return 'https://virtual.base.us-east.rpc.tenderly.co/935b2431-db1b-45b0-812a-e3debe0dadf2';
  }

  const roninAlchemyApiKey = process.env.ALCHEMY_API_KEY_2020?.trim();
  const rpcUrls: Record<
    Exclude<NetworkId, NetworkId.SMAUG>,
    string | undefined
  > = {
    [NetworkId.ETHEREUM]: `https://summer-serene-energy.quiknode.pro/${process.env.QUICKNODE_API_KEY_1}`,
    [NetworkId.POLYGON]: `https://falling-frosty-tent.matic.quiknode.pro/${process.env.QUICKNODE_API_KEY_137}`,
    [NetworkId.RONIN]: roninAlchemyApiKey
      ? `https://ronin-mainnet.g.alchemy.com/v2/${roninAlchemyApiKey}`
      : undefined,
    [NetworkId.OPTIMISM]: `https://ultra-summer-wave.optimism.quiknode.pro/${process.env.QUICKNODE_API_KEY_10}`,
    [NetworkId.ARBITRUM]: `https://little-divine-season.arbitrum-mainnet.quiknode.pro/${process.env.QUICKNODE_API_KEY_42161}`,
    [NetworkId.BASE]: `https://quaint-nameless-theorem.base-mainnet.quiknode.pro/${process.env.QUICKNODE_API_KEY_8453}`,
    [NetworkId.AVALANCHE]: `https://practical-fittest-haze.avalanche-mainnet.quiknode.pro/${process.env.QUICKNODE_API_KEY_43114}/ext/bc/C/rpc/`,
    /**
     * STAGE only RPC change - remove this after QA before merging DEN-357
     *
     * Replace back to https://virtual.mainnet.us-east.rpc.tenderly.co/abf000d0-3eb7-44c7-b2a6-bf182c31149b
     *
     * Testing involves accelerating (increasing the date) of the networks to circumvent 2 day timelock
     * I created an ethereum-fork-2 network in tenderly to test this
     * Accelerating is irreversible, so we we should not accelerate our sandbox networks
     */
    [NetworkId.ETHEREUM_FORK]: `https://virtual.mainnet.us-east.rpc.tenderly.co/d1d358dd-6c93-4808-aeee-0ded96978909`,
    [NetworkId.BASE_FORK]: `https://virtual.base.us-east.rpc.tenderly.co/e2a4167c-c4f9-42a2-82c6-ee2a79ce087e`,
    /**
     * STAGE only RPC change - remove this after QA before merging DEN-357
     *
     * Replace back to https://virtual.arbitrum.us-east.rpc.tenderly.co/18ae4397-5975-4185-bd0f-072b277a4cab
     *
     * Testing involves accelerating (increasing the date) of the networks to circumvent 2 day timelock
     * I created an arbitrum-fork-2 network in tenderly to test this
     * Accelerating is irreversible, so we we should not accelerate our sandbox networks
     */
    [NetworkId.ARBITRUM_FORK]: `https://virtual.arbitrum.us-east.rpc.tenderly.co/85daa133-164a-4c70-98c8-33fb19963a99`,
    [NetworkId.RONIN_FORK]: `https://virtual.ronin.eu.rpc.tenderly.co/d2cfdd03-5643-434a-9c7e-d224299c8623`,
    [NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK]:
      'https://virtual.mainnet.eu.rpc.tenderly.co/08196dad-e847-4b6c-aab3-008df09f72a9',
    [NetworkId.ETHEREUM_SEPOLIA]: 'https://ethereum-sepolia-rpc.publicnode.com',
  };
  const rpcUrl = rpcUrls[network as Exclude<NetworkId, NetworkId.SMAUG>];
  return isValidRpcUrl(rpcUrl) ? rpcUrl : null;
}

// Function to get the RPC URLs of a network. Defaults to private/preferred only.
function getNetworkRpcUrls(
  network: NetworkType,
  options: NetworkViemClientOptions = {}
): string[] {
  const privateRpcUrl = getPrivateNetworkRpcUrl(network);
  const publicRpcUrls = options.usePublicRpcUrls
    ? (PUBLIC_RPC_URLS[Number(network) as NetworkId] ?? [])
    : [];

  return Array.from(
    new Set([privateRpcUrl, ...publicRpcUrls].filter(isValidRpcUrl))
  );
}

function getPublicNetworkRpcUrls(network: NetworkType): string[] {
  return PUBLIC_RPC_URLS[Number(network) as NetworkId] ?? [];
}

// Function to get the RPC URL of a network
function getNetworkRpcUrl(network: NetworkType): string | null {
  return getNetworkRpcUrls(network)[0] ?? null;
}

/**
 * Builds an HTTP transport that rotates the starting RPC URL for each request.
 *
 * Each request can try every configured URL up to `retriesPerUrl` times. Public
 * URL failures are reported through `options.onPublicRpcFailure` while fallback
 * continues; logger failures are swallowed so they do not interrupt RPC calls.
 * Assumes callers pass at least one valid URL and a positive retry count.
 */
function roundRobinHttpTransport(
  network: NetworkType,
  urls: string[],
  publicUrls: string[],
  options: NetworkViemClientOptions = {},
  retriesPerUrl = PUBLIC_RPC_RETRIES_PER_URL
): Transport<'roundRobinHttp', { urls: string[] }> {
  let nextStartIndex = 0;
  const publicUrlSet = new Set(publicUrls);

  return ({ chain, timeout }) =>
    createTransport(
      {
        key: 'roundRobinHttp',
        name: 'Round-robin HTTP',
        request: (async ({
          method,
          params,
        }: {
          method: string;
          params?: unknown[];
        }) => {
          // Case: Rotate the starting URL so repeated requests do not always
          // prefer the same provider.
          const startIndex = nextStartIndex;
          nextStartIndex = (nextStartIndex + 1) % urls.length;
          const attemptsByUrl = new Array<number>(urls.length).fill(0);
          let lastError: unknown;

          for (
            let attempt = 0;
            attempt < urls.length * retriesPerUrl;
            attempt += 1
          ) {
            const urlIndex = (startIndex + attempt) % urls.length;
            // Case: This provider already reached its per-request retry cap.
            if ((attemptsByUrl[urlIndex] ?? 0) >= retriesPerUrl) {
              continue;
            }

            attemptsByUrl[urlIndex] = (attemptsByUrl[urlIndex] ?? 0) + 1;
            const url = urls[urlIndex];
            // Case: Defensive guard for sparse URL arrays.
            if (!url) {
              continue;
            }

            try {
              const transport = http(url, { retryCount: 0 })({
                chain,
                retryCount: 0,
                timeout,
              });
              const response = await transport.request({
                method,
                params,
              } as never);
              return response;
            } catch (error) {
              // Case: Public provider failed; report the transport failure and
              // explicitly ignore logger errors so fallback keeps progressing.
              if (publicUrlSet.has(url)) {
                Promise.resolve(
                  options.onPublicRpcFailure?.({
                    network_id: network,
                    error:
                      error instanceof Error ? error.message : String(error),
                    arbitrary_value: {
                      url,
                      method,
                      attempt: attemptsByUrl[urlIndex],
                      retriesPerUrl,
                    },
                  })
                ).catch(() => undefined);
              }
              lastError = error;
            }
          }

          throw lastError instanceof Error
            ? lastError
            : new Error('All RPC requests failed');
        }) as never,
        retryCount: 0,
        type: 'roundRobinHttp',
      },
      { urls }
    );
}

/** Return whether Smaug resolves to local Anvil instead of its Tenderly VNet. */
function usesLocalSmaugNetwork(): boolean {
  return (
    process.env.ENVIRONMENT === 'LOCAL' ||
    process.env.ENVIRONMENT === 'CI' ||
    process.env.ENVIRONMENT === 'DEMO'
  );
}

// Function to get the viem config of a network
function getNetworkViemConfig(network: NetworkType): Chain {
  const rpcUrl = getNetworkRpcUrl(network);

  // Handle Smaug specially - use Tenderly config for STAGE/PREPROD, local Anvil otherwise
  if (network === networkTypeSchema.parse(NetworkId.SMAUG)) {
    const smaugRpcUrl = usesLocalSmaugNetwork()
      ? 'http://localhost:8545'
      : 'https://virtual.base.us-east.rpc.tenderly.co/935b2431-db1b-45b0-812a-e3debe0dadf2';

    return {
      id: 8421,
      name: 'Smaug Testnet',
      nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
      rpcUrls: {
        default: {
          http: [smaugRpcUrl],
        },
      },
    } as const satisfies Chain;
  }

  // Handle Tenderly Virtual TestNet specially - use Tenderly config
  if (network === networkTypeSchema.parse(NetworkId.ETHEREUM_FORK)) {
    return {
      id: NetworkId.ETHEREUM_FORK,
      name: 'Ethereum Fork',
      nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
      rpcUrls: {
        default: {
          http: [rpcUrl || 'http://localhost:8545'],
        },
      },
    } as const satisfies Chain;
  }
  if (
    network ===
    networkTypeSchema.parse(NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK)
  ) {
    return {
      id: NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK,
      name: 'Ethereum Contract Deployment Fork',
      nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
      rpcUrls: {
        default: {
          http: [rpcUrl || 'http://localhost:8545'],
        },
      },
    } as const satisfies Chain;
  }
  if (network === networkTypeSchema.parse(NetworkId.BASE_FORK)) {
    return {
      id: NetworkId.BASE_FORK,
      name: 'Base Fork',
      nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
      rpcUrls: {
        default: {
          http: [rpcUrl || 'http://localhost:8545'],
        },
      },
    } as const satisfies Chain;
  }
  if (network === networkTypeSchema.parse(NetworkId.ARBITRUM_FORK)) {
    return {
      id: NetworkId.ARBITRUM_FORK,
      name: 'Arbitrum Fork',
      nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
      rpcUrls: {
        default: {
          http: [rpcUrl || 'http://localhost:8545'],
        },
      },
    } as const satisfies Chain;
  }
  if (network === networkTypeSchema.parse(NetworkId.RONIN_FORK)) {
    return {
      id: NetworkId.RONIN_FORK,
      name: 'Ronin Fork',
      nativeCurrency: { name: 'Ronin', symbol: 'RON', decimals: 18 },
      rpcUrls: {
        default: {
          http: [rpcUrl || 'http://localhost:8545'],
        },
      },
    } as const satisfies Chain;
  }

  const viemConfigs: Record<
    Exclude<
      NetworkId,
      | NetworkId.SMAUG
      | NetworkId.ETHEREUM_FORK
      | NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK
      | NetworkId.BASE_FORK
      | NetworkId.ARBITRUM_FORK
      | NetworkId.RONIN_FORK
    >,
    Chain
  > = {
    [NetworkId.ETHEREUM]: mainnet,
    [NetworkId.POLYGON]: polygon,
    [NetworkId.RONIN]: ronin,
    [NetworkId.OPTIMISM]: optimism,
    [NetworkId.ARBITRUM]: arbitrum,
    [NetworkId.BASE]: base,
    [NetworkId.AVALANCHE]: avalanche,
    [NetworkId.ETHEREUM_SEPOLIA]: sepolia,
  };
  // Cast the branded type back to the underlying enum value for indexing
  return viemConfigs[
    network as Exclude<
      NetworkId,
      | NetworkId.SMAUG
      | NetworkId.ETHEREUM_FORK
      | NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK
      | NetworkId.BASE_FORK
      | NetworkId.ARBITRUM_FORK
      | NetworkId.RONIN_FORK
    >
  ];
}

// Function to get the full viem client of a network
export function getNetworkViemClient(
  network: NetworkType,
  options: NetworkViemClientOptions = {}
): PublicClient | null {
  const rpcUrls = getNetworkRpcUrls(network, options);
  if (rpcUrls.length === 0) {
    return null;
  }
  const chainConfig = getNetworkViemConfig(network);
  if (!chainConfig) {
    return null;
  }
  const transport = roundRobinHttpTransport(
    network,
    rpcUrls,
    getPublicNetworkRpcUrls(network),
    options
  );
  if (!transport) {
    return null;
  }
  return createPublicClient({
    chain: chainConfig,
    transport,
  }) as PublicClient;
}

// Helper function to create branded network IDs
export function createNetworkId(id: NetworkId): NetworkType {
  return networkTypeSchema.parse(id);
}

/**
 * Convert a branded NetworkType to its enum NetworkId counterpart
 * without using ID type casts.
 */
export function toNetworkId(networkId: NetworkType): NetworkId {
  const rawNetworkId = Number(networkId);
  switch (rawNetworkId) {
    case NetworkId.ETHEREUM:
      return NetworkId.ETHEREUM;
    case NetworkId.OPTIMISM:
      return NetworkId.OPTIMISM;
    case NetworkId.POLYGON:
      return NetworkId.POLYGON;
    case NetworkId.RONIN:
      return NetworkId.RONIN;
    case NetworkId.BASE:
      return NetworkId.BASE;
    case NetworkId.ARBITRUM:
      return NetworkId.ARBITRUM;
    case NetworkId.AVALANCHE:
      return NetworkId.AVALANCHE;
    case NetworkId.SMAUG:
      return NetworkId.SMAUG;
    case NetworkId.ETHEREUM_FORK:
      return NetworkId.ETHEREUM_FORK;
    case NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK:
      return NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK;
    case NetworkId.BASE_FORK:
      return NetworkId.BASE_FORK;
    case NetworkId.ARBITRUM_FORK:
      return NetworkId.ARBITRUM_FORK;
    case NetworkId.RONIN_FORK:
      return NetworkId.RONIN_FORK;
    case NetworkId.ETHEREUM_SEPOLIA:
      return NetworkId.ETHEREUM_SEPOLIA;
    default:
      throw new Error(`Unsupported network ID: ${networkId}`);
  }
}

/** This is the most common symbolic way to represent native token, which does NOT have an address,
    but we DO want to represent native tokens with ContractToken and Address records, so use this address */
export const nativeTokenPseudoAddress: `0x${string}` =
  '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

export const nativeTokenDecimals = 18;

// Function to get the native token symbol of a network
export function getNetworkNativeTokenSymbol(network: NetworkType): string {
  const nativeTokenSymbols: Record<NetworkId, string> = {
    [NetworkId.ETHEREUM]: 'ETH',
    [NetworkId.POLYGON]: 'POL',
    [NetworkId.RONIN]: 'RON',
    [NetworkId.OPTIMISM]: 'ETH',
    [NetworkId.ARBITRUM]: 'ETH',
    [NetworkId.BASE]: 'ETH',
    [NetworkId.AVALANCHE]: 'AVAX',
    [NetworkId.SMAUG]: 'ETH',
    [NetworkId.ETHEREUM_FORK]: 'ETH',
    [NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK]: 'ETH',
    [NetworkId.BASE_FORK]: 'ETH',
    [NetworkId.ARBITRUM_FORK]: 'ETH',
    [NetworkId.RONIN_FORK]: 'RON',
    [NetworkId.ETHEREUM_SEPOLIA]: 'ETH',
  };
  // Cast the branded type back to the underlying enum value for indexing
  return nativeTokenSymbols[network as NetworkId];
}

// Function to get the native token logo URL for a network
// Smaug testnet uses ETH as native token (fork of Base)
export function getNetworkNativeTokenLogoUrl(
  network: NetworkType
): string | null {
  const nativeTokenLogos: Partial<Record<NetworkId, string>> = {
    [NetworkId.ETHEREUM]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.OPTIMISM]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.POLYGON]:
      'https://assets.coingecko.com/coins/images/4713/large/polygon.png',
    [NetworkId.RONIN]:
      'https://coin-images.coingecko.com/asset_platforms/images/45/large/ronin.png?1706606769',
    [NetworkId.BASE]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.SMAUG]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png', // Fork of Base, uses ETH
    [NetworkId.ETHEREUM_FORK]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.ETHEREUM_SEPOLIA]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.BASE_FORK]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.ARBITRUM_FORK]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.ARBITRUM]:
      'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    [NetworkId.AVALANCHE]:
      'https://assets.coingecko.com/coins/images/12559/large/Avalanche_Circle_RedWhite_Trans.png',
    [NetworkId.RONIN_FORK]:
      'https://coin-images.coingecko.com/asset_platforms/images/45/large/ronin.png?1706606769',
  };
  return nativeTokenLogos[network as NetworkId] ?? null;
}

type BlockExplorer =
  | { kind: 'public'; baseUrl: string }
  | { kind: 'tenderly-vnet'; baseUrl: string };

const PUBLIC_BLOCK_EXPLORERS: Partial<Record<NetworkId, string>> = {
  [NetworkId.ETHEREUM]: 'https://etherscan.io',
  [NetworkId.POLYGON]: 'https://polygonscan.com',
  [NetworkId.RONIN]: 'https://explorer.roninchain.com',
  [NetworkId.OPTIMISM]: 'https://optimistic.etherscan.io',
  [NetworkId.ARBITRUM]: 'https://arbiscan.io',
  [NetworkId.BASE]: 'https://basescan.org',
  [NetworkId.AVALANCHE]: 'https://snowtrace.io',
  [NetworkId.ETHEREUM_SEPOLIA]: 'https://sepolia.etherscan.io',
};

const TENDERLY_VNET_DASHBOARDS: Partial<Record<NetworkId, string>> = {
  [NetworkId.SMAUG]:
    'https://dashboard.tenderly.co/onchainden/mls/testnet/5647f7c5-4745-4c20-b31d-9a010a44816b',
  [NetworkId.ETHEREUM_FORK]:
    'https://dashboard.tenderly.co/onchainden/mls/testnet/d67749a5-bd4d-4b46-a43d-dfcde796d8ec',
  [NetworkId.BASE_FORK]:
    'https://dashboard.tenderly.co/onchainden/mls/testnet/3bd7248b-69d9-4a78-914b-a58222b3f066',
  [NetworkId.ARBITRUM_FORK]:
    'https://dashboard.tenderly.co/onchainden/mls/testnet/17f511ec-b88d-4bf2-ae79-7095d52371dd',
};

/** Resolve the explorer kind whose route shape is valid for this network. */
function getNetworkBlockExplorer(network: NetworkType): BlockExplorer | null {
  const networkId = network as NetworkId;
  const publicBaseUrl = PUBLIC_BLOCK_EXPLORERS[networkId];
  if (publicBaseUrl) {
    return { kind: 'public', baseUrl: publicBaseUrl };
  }

  // Case: Smaug is local Anvil in these environments and has no explorer.
  if (networkId === NetworkId.SMAUG && usesLocalSmaugNetwork()) {
    return null;
  }

  const tenderlyBaseUrl = TENDERLY_VNET_DASHBOARDS[networkId];
  return tenderlyBaseUrl
    ? { kind: 'tenderly-vnet', baseUrl: tenderlyBaseUrl }
    : null;
}

// Function to get the block explorer URL for a token/contract address
export function getTokenBlockExplorerUrl(
  address: string,
  network: NetworkType
): string | null {
  const explorer = getNetworkBlockExplorer(network);
  if (!explorer) return null;

  return explorer.kind === 'public'
    ? `${explorer.baseUrl}/token/${address}`
    : `${explorer.baseUrl}/contract/virtual/${address}`;
}

// Function to get the block explorer URL for an address when the explorer has
// one route that works for both wallets and contracts.
export function getAddressBlockExplorerUrl(
  address: string,
  network: NetworkType
): string | null {
  const explorer = getNetworkBlockExplorer(network);
  if (!explorer || explorer.kind !== 'public') return null;

  return `${explorer.baseUrl}/address/${address}`;
}

/** Link a confirmed transaction to a public explorer when the network has one. */
export function getTransactionBlockExplorerUrl(
  transactionHash: string,
  network: NetworkType
): string | null {
  const explorer = getNetworkBlockExplorer(network);
  return explorer?.kind === 'public'
    ? `${explorer.baseUrl}/tx/${transactionHash}`
    : null;
}

/**
 * Map of network IDs to the address of the wrapped native token
 * Used for token price lookups
 */
export const WRAPPED_NATIVE_TOKEN_BY_NETWORK: Partial<
  Record<NetworkId, `0x${string}`>
> = {
  [NetworkId.ETHEREUM]: toChecksumAddress(
    '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2'
  ),
  [NetworkId.OPTIMISM]: toChecksumAddress(
    '0x4200000000000000000000000000000000000006'
  ),
  [NetworkId.POLYGON]: toChecksumAddress(
    '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270'
  ),
  [NetworkId.RONIN]: toChecksumAddress(
    '0xe514d9deb7966c8be0ca922de8a064264ea6bcd4'
  ),
  [NetworkId.BASE]: toChecksumAddress(
    '0x4200000000000000000000000000000000000006'
  ),
  [NetworkId.ARBITRUM]: toChecksumAddress(
    '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1'
  ),
  [NetworkId.AVALANCHE]: toChecksumAddress(
    '0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7'
  ),
  [NetworkId.SMAUG]: toChecksumAddress(
    '0x4200000000000000000000000000000000000006'
  ),
  [NetworkId.ETHEREUM_FORK]: toChecksumAddress(
    '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2'
  ),
  [NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK]: toChecksumAddress(
    '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2'
  ),
  [NetworkId.BASE_FORK]: toChecksumAddress(
    '0x4200000000000000000000000000000000000006'
  ),
  [NetworkId.ARBITRUM_FORK]: toChecksumAddress(
    '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1'
  ),
  [NetworkId.RONIN_FORK]: toChecksumAddress(
    '0xe514d9deb7966c8be0ca922de8a064264ea6bcd4'
  ),
};
